import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Select,
  InputLabel,
  FormControl,
  Stack,
  MenuItem,
  Button,
} from "@mui/material";
import { useParams } from "react-router-dom";
import performRequest from "network/perform-request";
import { competitionsExportScheduleRoute, getCompetitionsFixtureRoute } from "network/api-routes";
import { useIntl } from "react-intl";

import ScheduleByVenue from "./ScheduleByVenue";
import ScheduleByDay from "./ScheduleByDay";
import RevaText from "components/common/RevaText";
import CompetitionCalendar from "../CompetitionCalendar";

function CompetitionFixture() {
  const { id } = useParams();
  const intl = useIntl();
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  const matchStages = {
    group: intl.formatMessage({ id: "Fase de Grupos" }),
    elimination: intl.formatMessage({ id: "Llave Final" }),
  };

  const fetchMatches = useCallback(() => {
    setLoading(true);
    performRequest("GET", getCompetitionsFixtureRoute(id))
      .then((res) => {
        setMatches(res.data.matches ?? []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    fetchMatches();
  }, [fetchMatches]);

  const exportCalendar = () => {
    performRequest("GET", competitionsExportScheduleRoute(id), { responseType: "blob" }).then((res) => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `calendario-evento-${id}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  const handleTabChange = (event, newValue) => setTabIndex(newValue);

  const tournamentOptions = useMemo(() => {
    const unique = new Map();
    matches.forEach((m) => {
      if (m.tournament?.id && !unique.has(m.tournament.id)) {
        unique.set(m.tournament.id, m.tournament.name);
      }
    });
    return Array.from(unique.entries()).map(([id, name]) => ({ id, name }));
  }, [matches]);

  const matchStageOptions = useMemo(() => {
    const unique = new Set();
    matches.forEach((m) => {
      if (m.stage_type) {
        unique.add(m.stage_type);
      }
    });
    return Array.from(unique).sort();
  }, [matches]);

  const scheduledGames = useMemo(() => {
    return matches.filter((g) => {
      const byTournament = selectedTournamentId ? g.tournament.id === selectedTournamentId : true;
      const byStage = selectedStage ? g.stage_type === selectedStage : true;
      return g.is_scheduled && byTournament && byStage;
    });
  }, [matches, selectedTournamentId, selectedStage]);

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <RevaText variant="h2" id="Calendario de partidos" />
        <Button variant="outlined" onClick={exportCalendar}>
          {intl.formatMessage({ id: "Exportar calendario" })}
        </Button>
      </Stack>
      <Tabs sx={{ mt: 1 }} value={tabIndex} onChange={handleTabChange}>
        <Tab label={intl.formatMessage({ id: "Calendario" })} />
        <Tab label={intl.formatMessage({ id: "Por complejo y cancha" })} />
        <Tab label={intl.formatMessage({ id: "Por día" })} />
      </Tabs>

      {tabIndex !== 0 && (
        <Stack direction="row" spacing={2} sx={{ my: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{intl.formatMessage({ id: "Torneo" })}</InputLabel>
            <Select
              value={selectedTournamentId ?? ""}
              label={intl.formatMessage({ id: "Torneo" })}
              onChange={(e) => setSelectedTournamentId(e.target.value || null)}
            >
              <MenuItem value="">{intl.formatMessage({ id: "Todos" })}</MenuItem>
              {tournamentOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{intl.formatMessage({ id: "Fase" })}</InputLabel>
            <Select
              value={selectedStage ?? ""}
              label={intl.formatMessage({ id: "Fase" })}
              onChange={(e) => setSelectedStage(e.target.value || null)}
            >
              <MenuItem value="">{intl.formatMessage({ id: "Todas" })}</MenuItem>
              {matchStageOptions.map((stage) => (
                <MenuItem key={stage} value={stage}>
                  {matchStages[stage]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      )}

      {scheduledGames.length === 0 ? (
        <Box mt={4}>
          <RevaText variant="body1" color="text.secondary">
            {intl.formatMessage({
              id: "No hay partidos programados. Asigna fecha, hora y lugar a los partidos de tus torneos para que aparezcan aquí.",
            })}
          </RevaText>
        </Box>
      ) : (
        <Box mt={2}>
          {tabIndex === 0 && <CompetitionCalendar competitionID={id} />}
          {tabIndex === 1 && <ScheduleByVenue matches={scheduledGames} refresh={fetchMatches} />}
          {tabIndex === 2 && <ScheduleByDay matches={scheduledGames} refresh={fetchMatches} />}
        </Box>
      )}
    </Box>
  );
}

export default CompetitionFixture;
